/**
 * TanStack Query hooks for `affiliate-referral-program`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	accrue,
	affiliateReferralProgramQueryKeys,
	createReferral,
	getProgram,
	getSummary,
	listLedger,
	listReferrals,
	updateReferral,
	upsertProgram,
	type AccrueBody,
	type AccrueResponse,
	type CreateReferralBody,
	type ListLedgerResponse,
	type ListReferralsResponse,
	type ProgramView,
	type ReferralStatus,
	type ReferralView,
	type SummaryView,
	type UpdateReferralBody,
	type UpsertProgramBody,
} from "./api";

export function useAffiliateReferralProgram(): UseQueryResult<ProgramView, Error> {
	return useQuery({
		queryKey: affiliateReferralProgramQueryKeys.program(),
		queryFn: getProgram,
	});
}

export function useAffiliateReferralProgramUpsert(): UseMutationResult<
	ProgramView,
	Error,
	UpsertProgramBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: upsertProgram,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateReferralProgramQueryKeys.all });
		},
	});
}

export function useAffiliateReferralsList(filters?: {
	referrer_id?: string;
	referred_id?: string;
	status?: ReferralStatus;
}): UseQueryResult<ListReferralsResponse, Error> {
	return useQuery({
		queryKey: affiliateReferralProgramQueryKeys.referrals(filters),
		queryFn: () => listReferrals(filters),
	});
}

export function useAffiliateReferralCreate(): UseMutationResult<
	ReferralView,
	Error,
	CreateReferralBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createReferral,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateReferralProgramQueryKeys.all });
		},
	});
}

export function useAffiliateReferralUpdate(): UseMutationResult<
	ReferralView,
	Error,
	{ id: string; body: UpdateReferralBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => updateReferral(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateReferralProgramQueryKeys.all });
		},
	});
}

export function useAffiliateReferralLedger(filters?: {
	ancestor_id?: string;
	limit?: number;
}): UseQueryResult<ListLedgerResponse, Error> {
	return useQuery({
		queryKey: affiliateReferralProgramQueryKeys.ledger(filters),
		queryFn: () => listLedger(filters),
	});
}

export function useAffiliateReferralSummary(
	affiliateId: string,
	enabled = true,
): UseQueryResult<SummaryView, Error> {
	return useQuery({
		queryKey: affiliateReferralProgramQueryKeys.summary(affiliateId),
		queryFn: () => getSummary(affiliateId),
		enabled: enabled && !!affiliateId,
	});
}

export function useAffiliateReferralAccrue(): UseMutationResult<
	AccrueResponse,
	Error,
	AccrueBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: accrue,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateReferralProgramQueryKeys.all });
		},
	});
}
